import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Footprints, Plus, Minus, Target, TrendingUp, Calendar, Lock, Award } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StepsPage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  const [steps, setSteps] = useState(0);
  const [goal, setGoal] = useState(10000);
  const [history, setHistory] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const c = {
    back: 'Tillbaka',
    title: 'Stegräknare',
    upgrade: 'Uppgradera för tillgång',
    upgradeDesc: 'Stegräknare ingår i premium.',
    upgradeBtn: 'Uppgradera nu',
    todaysSteps: 'Dagens steg',
    ofGoal: 'av',
    goal: 'Mål',
    addSteps: 'Lägg till steg',
    setSteps: 'Skriv antal',
    add: 'Lägg till',
    lastDays: 'Senaste 7 dagarna',
    noData: 'Ingen data ännu',
    goalReached: 'Målet nått!'
  };

  useEffect(() => {
    if (hasAccess) loadData();
  }, [hasAccess]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/api/steps`, { headers: getAuthHeader() });
      const today = new Date().toISOString().split('T')[0];
      const todayEntry = response.data.entries.find(e => e.date === today);
      if (todayEntry) setSteps(todayEntry.steps);
      setGoal(response.data.goal || 10000);
      setHistory(response.data.entries.slice(-7));
    } catch (error) {
      console.error('Error loading steps:', error);
    }
  };

  const saveData = async (newSteps, newGoal = goal) => {
    const today = new Date().toISOString().split('T')[0];
    try {
      await axios.post(`${API}/api/steps`, {
        date: today,
        steps: newSteps,
        goal: newGoal
      }, { headers: getAuthHeader() });
      
      // Update local history
      let updatedHistory = [...history];
      const todayIndex = updatedHistory.findIndex(h => h.date === today);
      if (todayIndex >= 0) {
        updatedHistory[todayIndex].steps = newSteps;
      } else {
        updatedHistory.push({ date: today, steps: newSteps, goal: newGoal });
      }
      setHistory(updatedHistory.slice(-7));
    } catch (error) {
      console.error('Error saving steps:', error);
    }
  };

  const addSteps = (amount) => {
    const newSteps = Math.max(0, steps + amount);
    setSteps(newSteps);
    saveData(newSteps);
  };

  const setCustomSteps = () => {
    const amount = parseInt(inputValue);
    if (!isNaN(amount) && amount >= 0) {
      setSteps(amount);
      saveData(amount);
      setInputValue('');
    }
  };

  const updateGoal = async (newGoal) => {
    setGoal(newGoal);
    try {
      await axios.put(`${API}/api/steps/goal?goal=${newGoal}`, {}, { headers: getAuthHeader() });
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const progress = Math.min((steps / goal) * 100, 100);
  const weeklyAverage = history.length > 0 
    ? Math.round(history.reduce((sum, d) => sum + d.steps, 0) / history.length)
    : 0;

  const handleBack = () => {
    navigate('/dashboard', { state: { expandCategory: 'body' } });
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="steps-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl font-medium">Stegräknare</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl font-medium">Uppgradera för tillgång</h2>
            <p className="text-muted-foreground text-base">Stegräknare ingår i premium.</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full" data-testid="upgrade-btn">
              Uppgradera nu
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="steps-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-medium">Stegräknare</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Intro */}
        <div className="text-center space-y-3 animate-fade-in">
          <Footprints className="w-12 h-12 mx-auto text-primary" />
          <h2 className="font-heading text-3xl font-medium">Varje steg räknas</h2>
          <p className="text-muted-foreground text-base max-w-lg mx-auto">
            Logga dina dagliga steg och följ din framgång mot målet.
          </p>
        </div>

        {/* Main Progress Circle */}
        <div className="bg-card rounded-2xl border border-border/50 p-8 text-center animate-slide-up">
          <div className="relative w-48 h-48 mx-auto">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="96"
                cy="96"
                r="88"
                fill="none"
                stroke="hsl(var(--secondary))"
                strokeWidth="12"
              />
              <circle
                cx="96"
                cy="96"
                r="88"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 88}
                strokeDashoffset={2 * Math.PI * 88 * (1 - progress / 100)}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-heading font-bold">{steps.toLocaleString()}</span>
              <span className="text-sm text-muted-foreground">av {goal.toLocaleString()} steg</span>
            </div>
          </div>
          
          {progress >= 100 && (
            <div className="mt-4 flex items-center justify-center gap-2 text-primary">
              <Award className="w-5 h-5" />
              <span className="font-medium">Dagens mål uppnått!</span>
            </div>
          )}
        </div>

        {/* Quick Add Buttons */}
        <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <h3 className="font-heading text-lg font-medium text-center">Lägg till steg</h3>
          
          <div className="flex justify-center gap-2 flex-wrap">
            {[500, 1000, 2000, 5000].map((amount) => (
              <Button
                key={amount}
                variant="outline"
                onClick={() => addSteps(amount)}
                className="rounded-full"
              >
                <Plus className="w-4 h-4 mr-1" />
                {amount.toLocaleString()}
              </Button>
            ))}
          </div>

          <div className="flex gap-2 items-center max-w-xs mx-auto">
            <input
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ange antal steg"
              className="flex-1 p-3 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 text-base text-center"
              data-testid="custom-steps-input"
            />
            <Button onClick={setCustomSteps} className="rounded-xl">
              Sätt
            </Button>
          </div>

          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => addSteps(-500)} className="rounded-full text-muted-foreground">
              <Minus className="w-4 h-4 mr-1" />
              500
            </Button>
            <Button variant="outline" onClick={() => setSteps(0)} className="rounded-full text-muted-foreground">
              Nollställ
            </Button>
          </div>
        </div>

        {/* Goal Setting */}
        <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="font-heading text-lg font-medium flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Dagligt mål
          </h3>
          <div className="flex justify-center gap-2 flex-wrap">
            {[5000, 7500, 10000, 12000, 15000].map((g) => (
              <Button
                key={g}
                variant={goal === g ? 'default' : 'outline'}
                onClick={() => updateGoal(g)}
                className="rounded-full"
              >
                {g.toLocaleString()}
              </Button>
            ))}
          </div>
        </div>

        {/* Weekly Stats */}
        {history.length > 0 && (
          <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <h3 className="font-heading text-lg font-medium flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Senaste 7 dagarna
            </h3>
            
            <div className="text-center mb-4">
              <span className="text-2xl font-heading font-bold">{weeklyAverage.toLocaleString()}</span>
              <span className="text-muted-foreground text-sm ml-2">steg/dag i snitt</span>
            </div>

            <div className="flex justify-between items-end h-32 gap-2">
              {history.map((day, index) => {
                const height = Math.max((day.steps / goal) * 100, 5);
                const dayName = new Date(day.date).toLocaleDateString('sv-SE', { weekday: 'short' });
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex flex-col items-center justify-end h-24">
                      <div
                        className={`w-full rounded-t-lg transition-all ${
                          day.steps >= goal ? 'bg-primary' : 'bg-primary/50'
                        }`}
                        style={{ height: `${Math.min(height, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">{dayName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="bg-primary/5 rounded-2xl p-6 animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <h3 className="font-heading text-lg font-medium mb-3">Tips för fler steg</h3>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Ta trapporna istället för hissen
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Gå en promenad på lunchen
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Parkera längre bort från entrén
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Ta ett samtal medan du går
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="text-center pt-8">
          <p className="text-sm text-muted-foreground">
            Varje steg du tar är ett steg mot bättre hälsa.
          </p>
        </div>
      </main>
    </div>
  );
}
